import React, { ReactNode, useEffect, useState } from "react";
import { MdAddAPhoto } from "react-icons/md";
import { CircularProgressWithLabel } from "./CircularProgressWithLabel";

interface CircularUploadFileProps {
  value?: string | null;
  onChange?: (file: File | null) => void;
  onSubmit?: (file: File) => Promise<void> | void;
  fileSize?: number;
  progressUpload?: number;
  onErrorLargeFile?: (file: File | null) => void;
  onErrorFileType?: (file: File | null) => void;
  acceptFile?: string;
  className?: string;
  disabled?: boolean;
  description?: ReactNode;
}

export const CircularUploadFile: React.FC<CircularUploadFileProps> = ({
  value,
  onChange,
  onSubmit,
  progressUpload,
  fileSize = 10,
  onErrorLargeFile,
  acceptFile,
  className = "",
  disabled = false,
  description = (
    <div className="text-xs text-action_light_active flex flex-col items-center justify-center text-center">
      <p>許可されたファイル形式：.jpeg、.jpg、.png、.gif</p>
      <p>最大サイズ：10 MB</p>
    </div>
  ),
  onErrorFileType,
  ...props
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (value && typeof value === "string" && value.startsWith("blob:")) {
      setPreviewUrl(value);
    } else if (value && typeof value === "string") {
      setPreviewUrl(value);
    } else {
      setPreviewUrl(undefined);
    }

    // Cleanup function
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [value]);

  const isValidFileType = (file: File) => {
    if (!acceptFile) return true;

    const acceptedTypes = acceptFile.split(",").map((type) => type.trim());
    return acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      return file.type.match(type.replace("*", ".*"));
    });
  };

  const processFile = async (file: File) => {
    const fileSizeMB = (file?.size || 0) / (1024 * 1024);

    if (fileSizeMB > fileSize) {
      onErrorLargeFile?.(file);
      return;
    }

    if (!isValidFileType(file)) {
      onErrorFileType?.(file);
      return;
    }

    // Cleanup old preview
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    // Create new preview URL
    const newPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(newPreviewUrl);

    // Call onChange and onSubmit
    onChange?.(file);
    if (onSubmit) {
      await onSubmit(file);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      await processFile(file);
    }

    // Reset input value to allow selecting the same file again
    event.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragOver to false if we're leaving the label element itself
    // This prevents flickering when dragging over child elements
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const file = files[0]; // Take only the first file

    if (file) {
      await processFile(file);
    }
  };

  return (
    <div className={`flex flex-col items-center relative gap-6 ${className}`}>
      <label
        className={`
          relative w-36 h-36 rounded-full border border-dashed transition-all duration-300 ease-in-out overflow-hidden p-2
          bg-white/80 cursor-pointer group
          ${
            disabled
              ? "opacity-50 cursor-not-allowed border-gray-300"
              : isDragOver
                ? "border-cyan scale-105"
                : "border-gray-300 hover:border-gray-400"
          }
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Preview Container */}
        <div className="w-full h-full rounded-full overflow-hidden relative">
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to a default avatar if image fails to load
              const target = e.target as HTMLImageElement;
              target.src =
                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjY0IiBjeT0iNTAiIHI9IjIwIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0yNCA5NkMyNCA4NS4yIDMyLjggNzYuNCA0My42IDc2LjRIODQuNEM5NS4yIDc2LjQgMTA0IDg1LjIgMTA0IDk2VjEyOEgyNFY5NloiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+";
            }}
          />
        </div>

        {/* Overlay Icon */}
        <div
          className={`
            absolute inset-2 flex flex-col items-center justify-center
            bg-grey-900/65 opacity-0 transition-opacity duration-300 rounded-full
            group-hover:opacity-100
            ${isDragOver ? "opacity-100 bg-cyan/75" : ""}
          `}
        >
          <MdAddAPhoto className="w-6 h-6 text-white mb-1" />
          <span className="text-xs text-white text-center">
            {isDragOver ? "ドロップしてアップロード" : "画像アップロード"}
          </span>
        </div>

        {/* Hidden Input */}
        <input
          type="file"
          accept={acceptFile || "image/*"}
          onChange={handleFileChange}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          style={{
            clip: "rect(0 0 0 0)",
            clipPath: "inset(50%)",
            height: "1px",
            overflow: "hidden",
            position: "absolute",
            bottom: 0,
            left: 0,
            whiteSpace: "nowrap",
            width: "1px",
          }}
          {...props}
        />
      </label>

      {description}

      {/* Progress Indicator */}
      {progressUpload !== undefined && progressUpload > 0 && (
        <CircularProgressWithLabel value={progressUpload} />
      )}
    </div>
  );
};
